import type { ReactNode } from "react";
import type { RecordErrorFields } from "~/components/usedBy/usedByHelpers";

import isNoChangesError from "~/helpers/isNoChangesError";
import logger from "~/helpers/logger";
import { QUEUED_VARIANT } from "~/helpers/offline/queueAnnouncement";
import classifyRtkError, { extractTopLevelGraphqlMessage } from "~/helpers/rtkErrorClassification";
import { stripInternalIdentifiers } from "~/helpers/surfaceMutationError";
import unauthorizedRetryPolicy from "~/helpers/unauthorizedRetryPolicy";
import { OpenSnack, OpenSnackOptions } from "~/providers/SnackProvider";

export interface MutationPayloadWithErrors {
	errors?: ReadonlyArray<{ code?: string | null; message?: string | null }> | null;
}

export const EVENTSTORE_UNAVAILABLE_CODE = "EVENTSTORE_UNAVAILABLE";

export const hasUncertainEventStoreWrite = (errors: MutationPayloadWithErrors["errors"]): boolean =>
	Boolean(errors?.some((error) => error.code === EVENTSTORE_UNAVAILABLE_CODE));

export type MutationRequest<TData> = PromiseLike<{ data?: TData; error?: unknown }>;

export type MutationErrorMessage<TPayload extends MutationPayloadWithErrors> =
	| string
	| ((errors: TPayload["errors"] | undefined) => string);

export interface MutationRefusal {
	action: (key: number | string | undefined) => ReactNode;
	message: string;
}

export interface RunMutationOptions {
	expectedError?: (error: unknown) => boolean;
	describeRefusal?: (errors: null | ReadonlyArray<RecordErrorFields> | undefined) => MutationRefusal | undefined;
	missingEntityMessage?: string;
	noChangesMessage?: string;
	noChangesSeverity?: "info" | "success";
	noChangesSilentSuccess?: boolean;
	queuedMessage?: string;
	silent?: boolean;
	successMessage?: string;
}

export type RunMutationResultOptions<TPayload extends MutationPayloadWithErrors = MutationPayloadWithErrors> = Pick<
	RunMutationOptions,
	"expectedError" | "missingEntityMessage" | "noChangesSilentSuccess" | "silent" | "successMessage"
> & {
	expectedRefusal?: (errors: TPayload["errors"] | undefined) => boolean;
	selectEntity?: (payload: TPayload) => unknown;
};

export type RunMutationValidationOptions = Pick<
	RunMutationOptions,
	"missingEntityMessage" | "silent" | "successMessage"
> & {
	describeSnackError?: (errors: NonNullable<MutationPayloadWithErrors["errors"]>) => string | undefined;
};

export type NoChangesSnack = Pick<RunMutationOptions, "noChangesMessage" | "noChangesSeverity">;

export interface RunMutationArgs<TData, TPayload extends MutationPayloadWithErrors> {
	errorMessage: MutationErrorMessage<TPayload>;
	openSnack: OpenSnack;
	options?: RunMutationOptions;
	request: MutationRequest<TData>;
	selectPayload: (data: TData) => TPayload | null | undefined;
}

export interface RunMutationEntityArgs<TData, TPayload extends MutationPayloadWithErrors, TEntity>
	extends RunMutationArgs<TData, TPayload> {
	queuedEntity?: TEntity;
	selectEntity: (payload: TPayload) => TEntity | null | undefined;
}

// wsapi returns domain errors over HTTP 200, so error holds the payload errors array whenever no transport error occurred.
export interface MutationRawResult<TData> {
	data?: TData;
	error?: unknown;
	errorMessage?: string;
	payloadErrors?: MutationPayloadWithErrors["errors"];
	transportError?: unknown;
}

// sessionRenewed: baseApi declined to replay the mutation, so the write never landed.
export interface MutationWriteResult {
	domainErrors?: MutationPayloadWithErrors["errors"];
	errorMessage?: string | null;
	ok: boolean;
	rejected?: boolean;
	sessionRenewed: boolean;
}

export interface MutationValidationResult<TPayload extends MutationPayloadWithErrors, TEntity> {
	entity: TEntity | null;
	errors: NonNullable<TPayload["errors"]>;
	ok: boolean;
	payload: TPayload | null;
	transportError: unknown;
}

const sanitizedTopLevelMessage = (transportError: unknown): string =>
	stripInternalIdentifiers(extractTopLevelGraphqlMessage(transportError) ?? "");

const ALREADY_LOGGED: OpenSnackOptions = { skipLog: true };

const resolveMutationErrorMessage = <TPayload extends MutationPayloadWithErrors>(
	errorMessage: MutationErrorMessage<TPayload>,
	payload: TPayload | null | undefined,
	transportError: unknown
): string => {
	const domainErrors = payload?.errors;

	if (typeof errorMessage !== "function") {
		return domainErrors?.[0]?.message || sanitizedTopLevelMessage(transportError) || errorMessage;
	}

	if (domainErrors?.length) return errorMessage(domainErrors);
	return sanitizedTopLevelMessage(transportError) || errorMessage(domainErrors);
};

interface MutationOutcome<TPayload, TEntity> {
	domainErrors?: MutationPayloadWithErrors["errors"];
	entity: TEntity | null;
	errorMessage: string | null;
	ok: boolean;
	payload: TPayload | null;
	rejected?: boolean;
	sessionRenewed: boolean;
}

const executeMutation = async <TData, TPayload extends MutationPayloadWithErrors, TEntity>({
	errorMessage,
	openSnack,
	options,
	queuedEntity,
	request,
	selectEntity,
	selectPayload
}: RunMutationArgs<TData, TPayload> & Partial<RunMutationEntityArgs<TData, TPayload, TEntity>>): Promise<
	MutationOutcome<TPayload, TEntity>
> => {
	const result = await request;
	const payload = result.data ? selectPayload(result.data) : undefined;
	const sessionRenewed = unauthorizedRetryPolicy.isMutationNotReplayedError(result.error);
	const resolveMessage = (): string => resolveMutationErrorMessage(errorMessage, payload, result.error);

	if (!result.error && !payload) {
		const message = options?.missingEntityMessage ?? resolveMessage();
		logger.error(message, "No payload returned");
		if (!options?.silent) openSnack(message, "error", false, undefined, undefined, ALREADY_LOGGED);
		return { entity: null, errorMessage: message, ok: false, payload: null, sessionRenewed };
	}

	if (result.error && options?.expectedError?.(result.error)) {
		if (options.queuedMessage && !options.silent) openSnack(options.queuedMessage, QUEUED_VARIANT);
		return { entity: queuedEntity ?? null, errorMessage: null, ok: true, payload: payload ?? null, sessionRenewed };
	}

	if (result.error || payload?.errors?.length) {
		if (
			(options?.noChangesMessage || options?.noChangesSilentSuccess) &&
			isNoChangesError(result.error, payload?.errors)
		) {
			if (options?.noChangesMessage) openSnack(options.noChangesMessage, options.noChangesSeverity ?? "info");
			return { entity: null, errorMessage: null, ok: true, payload: payload ?? null, sessionRenewed };
		}
		const refusal = options?.describeRefusal?.(payload?.errors);
		const message = refusal?.message ?? resolveMessage();
		logger.error(message, result.error || payload?.errors);
		if (!options?.silent) {
			if (refusal) openSnack(message, "error", false, refusal.action, undefined, ALREADY_LOGGED);
			else openSnack(message, "error", false, undefined, undefined, ALREADY_LOGGED);
		}
		return {
			domainErrors: payload?.errors,
			entity: null,
			errorMessage: message,
			ok: false,
			payload: null,
			rejected: classifyRtkError(result.error) === "client",
			sessionRenewed
		};
	}

	const entity = selectEntity && payload ? selectEntity(payload) : undefined;
	if (selectEntity && !entity) {
		const message = options?.missingEntityMessage ?? resolveMessage();
		logger.error(message, "Missing entity in payload");
		if (!options?.silent) openSnack(message, "error", false, undefined, undefined, ALREADY_LOGGED);
		return { entity: null, errorMessage: message, ok: false, payload: null, sessionRenewed };
	}

	if (options?.successMessage) openSnack(options.successMessage, "success");

	return { entity: entity ?? null, errorMessage: null, ok: true, payload: payload ?? null, sessionRenewed };
};

const runMutation = async <TData, TPayload extends MutationPayloadWithErrors>(
	args: RunMutationArgs<TData, TPayload>
): Promise<boolean> => (await executeMutation(args)).ok;

const runMutationForWrite = async <TData, TPayload extends MutationPayloadWithErrors>(
	args: RunMutationArgs<TData, TPayload>
): Promise<MutationWriteResult> => {
	const { domainErrors, errorMessage, ok, rejected, sessionRenewed } = await executeMutation(args);
	return { domainErrors, errorMessage, ok, rejected: rejected ?? false, sessionRenewed };
};

const runMutationForEntity = async <TData, TPayload extends MutationPayloadWithErrors, TEntity>(
	args: RunMutationEntityArgs<TData, TPayload, TEntity>
): Promise<TEntity | null> => (await executeMutation(args)).entity;

const runMutationOrThrow = async <TData, TPayload extends MutationPayloadWithErrors>(
	args: RunMutationArgs<TData, TPayload>
): Promise<TPayload | null> => {
	const outcome = await executeMutation({ ...args, options: { ...args.options, silent: true } });
	if (!outcome.ok) throw new Error(outcome.errorMessage ?? "Mutation failed");
	return outcome.payload;
};

const runMutationForResult = async <TData, TPayload extends MutationPayloadWithErrors>({
	errorMessage,
	openSnack,
	options,
	request,
	selectPayload
}: Omit<RunMutationArgs<TData, TPayload>, "options"> & { options?: RunMutationResultOptions<TPayload> }): Promise<
	MutationRawResult<TData>
> => {
	const result = await request;
	const payload = result.data ? selectPayload(result.data) : undefined;
	const resolveMessage = (): string => resolveMutationErrorMessage(errorMessage, payload, result.error);

	if (result.error && options?.expectedError?.(result.error))
		return { data: result.data, error: result.error, transportError: result.error };

	if (!result.error && !payload) {
		const message = resolveMessage();
		logger.error(message, "No payload returned");
		if (!options?.silent) openSnack(message, "error", false, undefined, undefined, ALREADY_LOGGED);
		return { data: result.data, error: message, errorMessage: message };
	}

	if (result.error || payload?.errors?.length) {
		const raw = {
			data: result.data,
			error: result.error ?? payload?.errors,
			payloadErrors: payload?.errors ?? undefined,
			transportError: result.error
		};
		if (options?.noChangesSilentSuccess && isNoChangesError(result.error, payload?.errors)) return raw;
		if (options?.expectedRefusal?.(payload?.errors)) return raw;
		const message = resolveMessage();
		logger.error(message, result.error || payload?.errors);
		if (!options?.silent) openSnack(message, "error", false, undefined, undefined, ALREADY_LOGGED);
		return { ...raw, errorMessage: message };
	}

	if (options?.selectEntity && payload && !options.selectEntity(payload)) {
		const message = options.missingEntityMessage ?? resolveMessage();
		logger.error(message, "Missing entity in payload");
		if (!options.silent) openSnack(message, "error", false, undefined, undefined, ALREADY_LOGGED);
		return { data: result.data, error: message, errorMessage: message };
	}

	if (options?.successMessage) openSnack(options.successMessage, "success");
	return { data: result.data };
};

const runMutationForValidation = async <TData, TPayload extends MutationPayloadWithErrors, TEntity>({
	errorMessage,
	openSnack,
	options,
	request,
	selectEntity,
	selectPayload
}: Omit<RunMutationEntityArgs<TData, TPayload, TEntity>, "options"> & {
	options?: RunMutationValidationOptions;
}): Promise<MutationValidationResult<TPayload, TEntity>> => {
	const result = await request;
	const payload = result.data ? selectPayload(result.data) : undefined;
	const errors = (payload?.errors ?? []) as NonNullable<TPayload["errors"]>;
	const resolveMessage = (): string => resolveMutationErrorMessage(errorMessage, payload, result.error);

	if (errors.length) {
		logger.error(resolveMessage(), errors);
		const snackMessage = options?.silent ? undefined : options?.describeSnackError?.(errors);
		if (snackMessage) openSnack(snackMessage, "error", false, undefined, undefined, ALREADY_LOGGED);
		return { entity: null, errors, ok: false, payload: payload ?? null, transportError: result.error };
	}

	if (result.error || !payload) {
		const message = resolveMessage();
		logger.error(message, result.error ?? "No payload returned");
		if (!options?.silent) openSnack(message, "error", false, undefined, undefined, ALREADY_LOGGED);
		return { entity: null, errors, ok: false, payload: payload ?? null, transportError: result.error };
	}

	const entity = selectEntity(payload) ?? null;
	if (!entity) {
		const message = options?.missingEntityMessage ?? resolveMessage();
		logger.error(message, "Missing entity in payload");
		if (!options?.silent) openSnack(message, "error", false, undefined, undefined, ALREADY_LOGGED);
		return { entity: null, errors, ok: false, payload, transportError: result.error };
	}

	if (options?.successMessage) openSnack(options.successMessage, "success");
	return { entity, errors, ok: true, payload, transportError: undefined };
};

const mutationRunner = {
	runMutation,
	runMutationForEntity,
	runMutationForWrite,
	runMutationForResult,
	runMutationForValidation,
	runMutationOrThrow
};

export default mutationRunner;
